package com.example.mystybag.appCore.expected

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.layout.boundsInWindow
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.ui.Modifier
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.core.expected.LogHelper
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.sharedmodels.project.responseModels.HomeTopPickUiModel
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement

/** Desktop web home map: match Figma / index.html; slot uses fillMaxHeight so bounds would be too tall. */
private const val WEB_HOME_MAP_OVERLAY_HEIGHT_PX = 700

private const val MAP_CONTAINER_ID = "mystybag-map-container"
private const val MAPS_SCRIPT_ID = "mystybag-google-maps-script"
private const val MAPS_INIT_CALLBACK = "mystybagMapsInit"
private const val MAP_SELECTED_CARD_ID = "mystybag-map-selected-card"

private var currentMapInstance: dynamic = null
private var currentMarkers: Array<dynamic> = arrayOf()
private var mapsScriptRequested = false
private val mapsReadyCallbacks = mutableListOf<() -> Unit>()

private fun hasGoogleMapsApi(): Boolean = js(
    "typeof window !== 'undefined' && " +
        "typeof window.google !== 'undefined' && " +
        "typeof window.google.maps !== 'undefined' && " +
        "typeof window.google.maps.Map !== 'undefined'"
).unsafeCast<Boolean>()

@Composable
actual fun LocationsMapView(
    modifier: Modifier,
    items: List<HomeTopPickUiModel>,
    searchQuery: String,
    onSearchQueryChange: (String) -> Unit,
    userLatitude: Double?,
    userLongitude: Double?,
    onLocationClick: (HomeTopPickUiModel) -> Unit,
    onLocationCardClick: (HomeTopPickUiModel) -> Unit,
    onClusterClick: ((List<HomeTopPickUiModel>) -> Unit)?,
    selectedItemId: String?
) {
    val apiKey = (window.asDynamic().MystyBagGoogleMapsApiKey as? String).orEmpty()
    val selectedItem = selectedItemId?.let { selectedId -> items.firstOrNull { it.id == selectedId } }

    DisposableEffect(apiKey) {
        val container = ensureMapContainer()
        if (container != null) {
            applyHomeMapDomOverlayChrome(container)
            applyHomeMapDomOverlayLayoutDefault(container)
        }
        LogHelper.logEvent("[LocationsMapView] DisposableEffect: container=${container != null} apiKeyLen=${apiKey.length} items=${items.size}")
        // Do not toggle .visible here: HorizontalPager keeps this composable alive on other tabs, and
        // MainView's SideEffect runs before this DisposableEffect can re-show the overlay after hide.
        onDispose {
            hideMapContainerImmediate()
            clearMap()
        }
    }

    LaunchedEffect(apiKey, items, userLatitude, userLongitude) {
        if (apiKey.isEmpty()) return@LaunchedEffect
        val container = document.getElementById(MAP_CONTAINER_ID) as? HTMLElement ?: return@LaunchedEffect
        applyHomeMapDomOverlayChrome(container)
        initGoogleMap(container, items, userLatitude, userLongitude, onLocationClick)
    }

    SideEffect {
        if (apiKey.isEmpty()) return@SideEffect
        val container = document.getElementById(MAP_CONTAINER_ID) as? HTMLElement ?: return@SideEffect
        applyHomeMapDomOverlayChrome(container)
        if (selectedItem == null) {
            removeSelectedLocationCard()
        } else {
            renderSelectedLocationCard(
                container = container,
                item = selectedItem,
                onCardClick = { onLocationCardClick(selectedItem) }
            )
        }
    }

    if (apiKey.isEmpty()) {
        Box(
            modifier = modifier.fillMaxSize(),
            content = {
                Texts.BodyMedium(
                    text = sharedString(SR.strings.map_web_placeholder),
                    color = AppTheme.current.colors.onSurfaceVariant,
                    modifier = Modifier
                )
            }
        )
        return
    }

    Box(
        modifier = modifier
            .fillMaxSize()
            .onGloballyPositioned { coordinates ->
                val container = document.getElementById(MAP_CONTAINER_ID) as? HTMLElement ?: return@onGloballyPositioned
                val rect = coordinates.boundsInWindow()
                val w = rect.width.toInt()
                if (w <= 0) return@onGloballyPositioned
                applyHomeMapDomOverlayChrome(container)
                applyHomeMapDomOverlayLayoutFromWindowBounds(container, rect)
            }
    )
}

/** Non-geometry styles (safe to re-apply from LaunchedEffect without fighting layout sync). */
private fun applyHomeMapDomOverlayChrome(container: HTMLElement) {
    val overlayBlocked = (window.asDynamic().__mystybagBottomSheetOpen as? Boolean == true) ||
        (window.asDynamic().__mystybagDialogOpen as? Boolean == true)
    // Google Maps may inject inline `position: relative` on the container.
    container.style.setProperty("position", "fixed", "important")
    container.style.setProperty("z-index", if (overlayBlocked) "0" else "2147483647", "important")
    container.style.setProperty("pointer-events", if (overlayBlocked) "none" else "auto", "important")
    container.style.setProperty("border-radius", "32px", "important")
    container.style.setProperty("overflow", "hidden", "important")
}

private fun applyHomeMapDomOverlayLayoutDefault(container: HTMLElement) {
    container.style.setProperty("left", "calc((100vw - 1080px) / 2 + 552px)", "important")
    container.style.setProperty("top", "163px", "important")
    container.style.setProperty("width", "528px", "important")
    container.style.setProperty("height", "${WEB_HOME_MAP_OVERLAY_HEIGHT_PX}px", "important")
}

private fun applyHomeMapDomOverlayLayoutFromWindowBounds(container: HTMLElement, rect: Rect) {
    val slotHeight = rect.height.toInt().coerceAtLeast(1)
    val mapHeightPx = minOf(slotHeight, WEB_HOME_MAP_OVERLAY_HEIGHT_PX)
    container.style.setProperty("left", "${rect.left.toInt()}px", "important")
    container.style.setProperty("top", "${rect.top.toInt()}px", "important")
    container.style.setProperty("width", "${rect.width.toInt()}px", "important")
    container.style.setProperty("height", "${mapHeightPx}px", "important")
}

private fun initGoogleMap(
    container: HTMLElement,
    items: List<HomeTopPickUiModel>,
    userLatitude: Double?,
    userLongitude: Double?,
    onLocationClick: (HomeTopPickUiModel) -> Unit
) {
    val mapsApiReady = hasGoogleMapsApi()
    LogHelper.logEvent("[LocationsMapView] initGoogleMap: mapsApiReady=$mapsApiReady")
    if (!mapsApiReady) {
        LogHelper.logEvent("[LocationsMapView] loadMapsScript: loading script...")
        loadMapsScript {
            LogHelper.logEvent("[LocationsMapView] mystybagMapsInit callback: entering createMap")
            createMap(container, items, userLatitude, userLongitude, onLocationClick)
        }
    } else {
        createMap(container, items, userLatitude, userLongitude, onLocationClick)
    }
}

private fun loadMapsScript(callback: () -> Unit) {
    val apiKey = (window.asDynamic().MystyBagGoogleMapsApiKey as? String).orEmpty()
    if (apiKey.isEmpty()) return
    if (hasGoogleMapsApi()) {
        callback()
        return
    }

    mapsReadyCallbacks += callback
    if (mapsScriptRequested) return

    mapsScriptRequested = true
    window.asDynamic()[MAPS_INIT_CALLBACK] = {
        mapsScriptRequested = false
        val pending = mapsReadyCallbacks.toList()
        mapsReadyCallbacks.clear()
        pending.forEach { it() }
    }

    val existingScript = document.getElementById(MAPS_SCRIPT_ID)
    if (existingScript != null) return

    val script = document.createElement("script")
    script.id = MAPS_SCRIPT_ID
    script.asDynamic().async = true
    script.asDynamic().defer = true
    script.asDynamic().src =
        "https://maps.googleapis.com/maps/api/js?key=$apiKey&callback=$MAPS_INIT_CALLBACK&loading=async&libraries=places"
    script.asDynamic().onerror = {
        LogHelper.logEvent("[LocationsMapView] loadMapsScript ERROR: failed to load Google Maps API script")
        mapsScriptRequested = false
    }
    document.head?.appendChild(script)
}

private fun createMap(
    container: HTMLElement,
    items: List<HomeTopPickUiModel>,
    userLatitude: Double?,
    userLongitude: Double?,
    onLocationClick: (HomeTopPickUiModel) -> Unit
) {
    if (!hasGoogleMapsApi()) {
        LogHelper.logEvent("[LocationsMapView] createMap: google maps API not ready, abort")
        return
    }
    val google = window.asDynamic().google
    LogHelper.logEvent("[LocationsMapView] createMap: start centerLat/centerLng from items=${items.size}")

    val centerLat = userLatitude ?: items.firstOrNull()?.locationLatitude ?: 43.2141
    val centerLng = userLongitude ?: items.firstOrNull()?.locationLongitude ?: 27.9147
    LogHelper.logEvent("[LocationsMapView] createMap: centerLat=$centerLat centerLng=$centerLng")

    val containerW = container.asDynamic().offsetWidth as? Int ?: 0
    val containerH = container.asDynamic().offsetHeight as? Int ?: 0
    val hasVisible = container.classList.contains("visible")
    LogHelper.logEvent("[LocationsMapView] createMap: container size=${containerW}x${containerH} hasVisible=$hasVisible")

    try {
        val map = currentMapInstance ?: run {
            // Map constructor: pass container by ID so Maps API gets a native Element (avoids IntersectionObserver "parameter 1 is not of type 'Element'" in some envs)
            val mapContainer = document.getElementById(MAP_CONTAINER_ID)
            if (mapContainer == null || !js("(mapContainer instanceof Element)").unsafeCast<Boolean>()) {
                LogHelper.logEvent("[LocationsMapView] createMap: container missing or not an Element, skip")
                return
            }
            LogHelper.logEvent("[LocationsMapView] createMap: creating Map(container) no options...")
            val createdMap = js("new google.maps.Map(document.getElementById('mystybag-map-container'))").unsafeCast<dynamic>()
            currentMapInstance = createdMap
            createdMap
        }
        if (currentMapInstance === map) {
            LogHelper.logEvent("[LocationsMapView] createMap: Map ready, updating center and markers...")
            val center = js("new google.maps.LatLng(centerLat, centerLng)").unsafeCast<dynamic>()
            map.setCenter(center)
            if (currentMarkers.isEmpty()) {
                map.setZoom(12)
            }
        }
        updateMapMarkers(map = map, items = items, onLocationClick = onLocationClick, google = google)
        LogHelper.logEvent("[LocationsMapView] createMap: done")
    } catch (e: Throwable) {
        LogHelper.logEvent("[LocationsMapView] createMap ERROR: ${e.message} at ${e.stackTraceToString().take(300)}")
    }
}

private fun updateMapMarkers(
    map: dynamic,
    items: List<HomeTopPickUiModel>,
    onLocationClick: (HomeTopPickUiModel) -> Unit,
    google: dynamic
) {
    clearMarkers()

    val bounds = js("new google.maps.LatLngBounds()").unsafeCast<dynamic>()
    val markersList = mutableListOf<dynamic>()
    LogHelper.logEvent("[LocationsMapView] updateMapMarkers: itemsWithCoords=${items.count { it.locationLatitude != 0.0 && it.locationLongitude != 0.0 }}")

    // Зелен маркер (Figma) – не default red; използваме зелена иконка от Google
    val greenIconUrl = "https://maps.google.com/mapfiles/ms/micons/green-dot.png"

    items.forEach { item ->
        if (item.locationLatitude != 0.0 && item.locationLongitude != 0.0) {
            val lat = item.locationLatitude
            val lng = item.locationLongitude
            val position = js("new google.maps.LatLng(lat, lng)").unsafeCast<dynamic>()
            bounds.extend(position)
            val opts = js("{}").unsafeCast<dynamic>()
            opts.position = position
            opts.map = map
            opts.title = item.title
            opts.icon = greenIconUrl
            val marker = js("new google.maps.Marker(opts)").unsafeCast<dynamic>()
            marker.addListener("click") {
                onLocationClick(item)
            }
            markersList.add(marker)
        }
    }

    currentMarkers = markersList.toTypedArray()
    LogHelper.logEvent("[LocationsMapView] updateMapMarkers: markers added=${markersList.size}")

    if (markersList.isNotEmpty()) {
        map.fitBounds(bounds)
        var listener: dynamic = null
        listener = map.addListener("idle") {
            val zoom = map.getZoom()
            if (zoom > 15) map.setZoom(15)
            google.maps.event.removeListener(listener)
        }
    }
}

private fun clearMarkers() {
    currentMarkers.forEach { it?.setMap(null) }
    currentMarkers = arrayOf()
}

private fun clearMap() {
    clearMarkers()
    currentMapInstance = null
}

private fun renderSelectedLocationCard(
    container: HTMLElement,
    item: HomeTopPickUiModel,
    onCardClick: () -> Unit
) {
    val card = (document.getElementById(MAP_SELECTED_CARD_ID) as? HTMLElement)
        ?: (document.createElement("div") as HTMLElement).also {
            it.id = MAP_SELECTED_CARD_ID
            container.appendChild(it)
        }

    card.style.position = "absolute"
    card.style.left = "12px"
    card.style.right = "12px"
    card.style.bottom = "12px"
    card.style.zIndex = "5"
    card.style.background = "#FFFFFF"
    card.style.borderRadius = "24px"
    card.style.boxShadow = "0 12px 28px rgba(0,0,0,0.22)"
    card.style.padding = "12px"
    card.style.cursor = "pointer"
    card.style.asDynamic().pointerEvents = "auto"
    card.style.fontFamily = "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif"

    val imageUrl = item.locationCoverUrl?.trim().orEmpty()
    val imageHtml =
        if (imageUrl.isNotEmpty()) {
            """
                <img
                    src="${escapeHtmlAttribute(imageUrl)}"
                    alt=""
                    style="width:100px;height:116px;object-fit:cover;border-radius:12px;display:block;flex:0 0 100px;"
                />
            """.trimIndent()
        } else {
            """
                <div style="width:100px;height:116px;border-radius:12px;background:#F3F4F6;flex:0 0 100px;"></div>
            """.trimIndent()
        }

    val secondaryText = listOf(item.subtitle, item.distance, item.collectInfo)
        .map { it.trim() }
        .filter { it.isNotEmpty() }
        .joinToString(" · ")

    val oldPriceHtml = item.oldPrice.trim().takeIf { it.isNotEmpty() }?.let {
        "<div style=\"font-size:12px;color:#6B7280;text-decoration:line-through;\">${escapeHtmlText(it)}</div>"
    }.orEmpty()
    val newPriceHtml = item.newPrice.trim().takeIf { it.isNotEmpty() }?.let {
        "<div style=\"font-size:18px;font-weight:700;color:#24A148;line-height:1.2;\">${escapeHtmlText(it)}</div>"
    }.orEmpty()

    card.innerHTML = """
        <div style="display:flex;gap:12px;align-items:center;">
            $imageHtml
            <div style="min-width:0;flex:1;display:flex;flex-direction:column;gap:6px;">
                <div style="font-size:17px;font-weight:700;color:#111827;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                    ${escapeHtmlText(item.title)}
                </div>
                ${
        if (secondaryText.isNotEmpty()) {
            "<div style=\"font-size:13px;color:#6B7280;line-height:1.25;\">${escapeHtmlText(secondaryText)}</div>"
        } else {
            ""
        }
    }
                <div style="display:flex;align-items:baseline;gap:8px;">
                    $oldPriceHtml
                    $newPriceHtml
                </div>
            </div>
        </div>
    """.trimIndent()

    card.onclick = {
        // Hide the fixed map overlay before Compose/decompose navigation starts
        // to avoid one-frame visual overlap with the Bag screen transition.
        hideMapContainerImmediate()
        onCardClick()
        Unit
    }
}

private fun removeSelectedLocationCard() {
    val card = document.getElementById(MAP_SELECTED_CARD_ID) as? HTMLElement ?: return
    card.parentElement?.removeChild(card)
}

private fun hideMapContainerImmediate() {
    val container = document.getElementById(MAP_CONTAINER_ID) as? HTMLElement ?: return
    container.classList.remove("visible")
    // !important beats #mystybag-map-container.visible { display: block !important } if class is re-added out of order.
    container.style.setProperty("display", "none", "important")
    container.style.setProperty("visibility", "hidden", "important")
    container.style.setProperty("opacity", "0", "important")
    container.style.setProperty("pointer-events", "none", "important")
    removeSelectedLocationCard()
}

private fun escapeHtmlText(value: String): String =
    value
        .replace("&", "&amp;")
        .replace("<", "&lt;")
        .replace(">", "&gt;")
        .replace("\"", "&quot;")
        .replace("'", "&#39;")

private fun escapeHtmlAttribute(value: String): String = escapeHtmlText(value)

private fun ensureMapContainer(): HTMLElement? {
    val existing = document.getElementById(MAP_CONTAINER_ID) as? HTMLElement
    if (existing != null) return existing

    val root = document.getElementById("root") as? HTMLElement ?: document.body as? HTMLElement ?: return null
    val created = document.createElement("div") as HTMLElement
    created.id = MAP_CONTAINER_ID
    root.appendChild(created)
    return created
}
